<template>
  <div
    class="tabs-item"
    :class="containerClasses"
  >
    <div
      v-if="isEditMode"
      class="tabs-item__input"
      :class="inputClasses"
      @click.stop
    >
      <UIInputText
        ref="inputRef"
        v-model="configName"
        size="xs"
        @click:enter="isEditMode = false"
        @click:tab="isEditMode = false"
        @click:escape="isEditMode = false"
        @ready="(e: HTMLInputElement) => e.focus()"
      />
    </div>
    <template v-else>
      <Bars3Icon
        v-if="!isLastOne && !readonly"
        aria-hidden="true"
        class="tabs-item__handle"
        @click.stop
      />
      <div
        ref="labelRef"
        v-tooltip.permanent="tooltip"
        class="tabs-item__name"
        v-text="label"
      />
      <UIDropdownActions
        v-if="!readonly"
        :data
        placement="top-end"
        popper-class="tabs-item__popper"
      >
        <template #default="{ toggle }">
          <UIButtonMenu
            class="tabs-item__menu"
            @click.stop="toggle"
          />
        </template>
        <template
          v-if="isRemoveMode"
          #popper
        >
          <div class="tabs-item__confirm">
            <span>{{ DELETE_CONFIG_CONFIRMATION_TEXT }}</span>
            <div class="tabs-item__confirm__buttons">
              <UIButton
                label="Yes"
                variant="danger"
                size="xs"
                @click="handleDeleteConfirm"
              />
              <UIButton
                label="No"
                variant="secondary"
                size="xs"
                @click="handleDeleteCancel"
              />
            </div>
          </div>
        </template>
      </UIDropdownActions>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'

import { TabsItemLabel } from './utils/enums'
import { DELETE_CONFIG_CONFIRMATION_TEXT } from './utils/const'

import {
  Bars3Icon,
  PaperClipIcon,
  PencilIcon,
  Square2StackIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import { UIButton, UIButtonMenu } from '@ui/buttons'
import { UIInputText } from '@ui/fields'
import { UIDropdownActions } from '@ui/dropdowns'
import { MaybeElement, onClickOutside, useResizeObserver } from '@vueuse/core'
import { ButtonVariant } from '@types'

type Props = {
  label: string
  isLastOne: boolean
  active?: boolean
  editOnRender?: boolean
  readonly?: boolean
}

type Emits = {
  duplicate: []
  remove: []
  update: [data: string]
  pin: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  edit() {
    isEditMode.value = true
  },
})

const labelRef = useTemplateRef('labelRef')
const inputRef = useTemplateRef<MaybeElement>('inputRef')

const configName = ref('')
const isEditMode = ref(false)
const isRemoveMode = ref(false)
const isLabelEllipsis = ref(false)

const data = computed(() => {
  return [
    {
      label: TabsItemLabel.DUPLICATE,
      icon: Square2StackIcon,
      action: () => emit('duplicate'),
    },
    {
      label: TabsItemLabel.RENAME,
      icon: PencilIcon,
      action: () => (isEditMode.value = true),
    },
    ...(props.isLastOne
      ? []
      : [
        {
          label: TabsItemLabel.PIN_TO_DASHBOARDS,
          icon: PaperClipIcon,
          action: () => {
            emit('pin')
          }
        },
        {
          label: TabsItemLabel.REMOVE,
          icon: TrashIcon,
          action: () => (isRemoveMode.value = true),
          variant: 'danger' as ButtonVariant,
        },
      ]),
  ]
})

const tooltip = computed(() => {
  if (!isLabelEllipsis.value) return

  return {
    content: props.label,
    handleResize: true,
  }
})

const containerClasses = computed(() => ({
  'tabs-item--active': props.active,
  'tabs-item--last-one': props.isLastOne,
  'tabs-item--readonly': props.readonly,
}))

const inputClasses = computed(() => ({
  'tabs-item__input--last-one': props.isLastOne,
}))

useResizeObserver(labelRef, () => {
  if (!props.label || !labelRef.value) return
  isLabelEllipsis.value =
    labelRef.value.offsetWidth < labelRef.value.scrollWidth
})

const handleDeleteConfirm = () => {
  emit('remove')
}

const handleDeleteCancel = () => {
  isRemoveMode.value = false
}

watch(isEditMode, value => {
  if (value) {
    configName.value = props.label
  } else if (configName.value !== props.label) {
    emit('update', configName.value)
  }
})

onClickOutside(inputRef, () => {
  isEditMode.value = false
})

onMounted(() => {
  if (props.editOnRender) isEditMode.value = true
})
</script>

<style>
.tabs-item {
  @apply flex items-center;
  @apply gap-1;
  @apply p-1;
  @apply bg-gray-100 hover:bg-gray-200;
  @apply dark:bg-gray-750 dark:hover:bg-gray-700;
  @apply cursor-pointer;

  &--active {
    @apply bg-indigo-100! dark:bg-indigo-900!;
    @apply text-indigo-700 dark:text-indigo-300;
  }

  &--last-one {
    @apply pl-3;
  }

  &--readonly {
    @apply py-1.5 px-3;
  }

  &__input {
    @apply -m-0.5;
    @apply min-w-32;

    &--last-one {
      @apply -ml-2.5;
    }
  }

  &__handle {
    @apply w-4 h-4;
    @apply mr-1;
    @apply text-gray-400;
  }

  &__name {
    @apply max-w-[20rem];
    @apply text-base sm:text-sm;
    @apply truncate;
  }

  &__menu {
    @apply px-0! -my-0.5;
  }

  &__confirm {
    @apply w-44;
    @apply p-2;
    @apply text-sm sm:text-xs;
    @apply text-light-text;
    @apply text-center;

    &__buttons {
      @apply flex justify-center;
      @apply gap-2;
      @apply pt-2;
    }
  }

  &__popper {
    .ui-dropdown-list__container {
      @apply my-0;
    }

    .v-popper__arrow-container {
      @apply visible;
    }
  }
}
</style>
